
import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getLocale, useIntl } from 'umi';
import CardBox from "@/pages/dashboard/components/CardBox";
import BarChart from "@sg/components/ChartLive/components/BarChart";
import LineChart from "@sg/components/ChartLive/components/LineChart";
import MiniTable from "@/pages/dashboard/components/MiniTable";
import { addChartLabel, xAxisHighLight, xAxisLabelFormat } from "@sg/components/ChartLive/_config/plug";
import { handleDownload } from "@/pages/dashboard/utils/utils";
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { useWeekDay } from '@/pages/dashboard/utils/customHook';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { axisLabelFormatter, downValueFormatter } from '@/pages/benchmark/utils/utils';
import { IMetricsTrendChart } from './types';
import { getLanguage } from '@home/utils/common';
import { getMetricsName } from './_utils';
import { LANGUAGE_EN } from '@home/_config/config';
import CommonBubble from '@home/components/CommonBubble';
import FormatHtmlTextToReact from '@home/components/FormatHtmlTextToReact';
import { chartAllStudioColors } from '@sg/utils/useChartOptions'
import './index.scss';
import { color } from 'echarts';

const MetricsTrendChart: React.FC<IMetricsTrendChart> = (props) => {
  const {
    className = '',
    style = {},
    metricData,
    loading,
    error,
    filterVal,
    componentMetrics = [],
    titleEn,
    titleCn,
    subtitleEn,
    subtitleCn,
    tooltipEn,
    tooltipCn,
    downloadTitleEn,
    downloadTitleCn,
    bubbleTitleEn,
    bubbleTitleCn,
    height = 340,
    bordered = false,
    defaultAvtive = 0,
    showConfig,
    setIconType,
    setOtherConfigFunc,
    formatterFunc: toolTipFunc,
    labelFormatRule,
    yAxisFormatRule,
    table: propsTable,
    isOnlyChart = false,
    isReverse = false,
  } = props;
  const {
    showEvent = false,
    showEye = false,
    showBarChart = false,
    showLineChart = false,
    showTable = false,
    showDownloadTable = false,
    showBubbleTitle = false,
  } = showConfig || {};

  const { getWeekDay } = useWeekDay();
  const { formatMessage } = useIntl(); // 中英切换方法
  const isEn = getLocale() === LANGUAGE_EN;
  const { timeType = 'Day' } = filterVal || {};
  const [iseye, setIseye] = useState(true);
  const [isEvent, setIsEvent] = useState(true);

  useEffect(() => {
    setIsEvent(true);
    setIseye(true);
  }, [filterVal]);

  const handleAxisFormat = (value: moment.MomentInput) => timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');

  // 标题文案处理
  const { title, subtitle, downloadTitle, bubbleTitle, bubbleMetrics } = useMemo(() => {
    const title = getLanguage(isEn, titleEn, titleCn);
    const tooltip = getLanguage(isEn, tooltipEn, tooltipCn);
    const subtitle = getLanguage(isEn, subtitleEn, subtitleCn);
    const downloadTitle = getLanguage(isEn, downloadTitleEn, downloadTitleCn) || title;
    const bubbleTitle = getLanguage(isEn, bubbleTitleEn, bubbleTitleCn) || title;
    const bubbleMetrics: any[] = [];
    // 浮窗配置设置，用于添加对比趋势的配置
    componentMetrics?.map((metric: any) => {
      const { metricName, compareName, toolTipName, compareToolTipName, } = getMetricsName(metric, isEn) || {};
      bubbleMetrics?.push({ ...metric, name: metricName, toolTipName });
      if (metric?.isCompare) {
        bubbleMetrics?.push({ ...metric, name: compareName, toolTipName: compareToolTipName });
      }
    });
    return {
      title: tooltip ? <CommonBubble content={<FormatHtmlTextToReact text={tooltip} />}>{title}</CommonBubble> : title,
      subtitle,
      downloadTitle,
      bubbleTitle,
      bubbleMetrics,
    };
  }, [isEn, titleEn, titleCn, subtitleEn, subtitleCn, downloadTitleEn, downloadTitleCn, bubbleTitleEn, bubbleTitleCn, componentMetrics, tooltipEn,
    tooltipCn,]);

  // 趋势图数据处理
  const { xAxisData = [], legendData = [], barSeries = [], lineSeries = [], isNodata = true, table = { option: [], data: [], downloadData: [] } } = useMemo(() => {
    const xAxisData: any[] = metricData?.map((item: any) => item?.date) || [];
    const legendData: any[] = [];
    const barSeries: any[] = [];
    const lineSeries: any[] = [];
    const tableData: any[] = [];
    const tableDownloadData: any[] = [];
    let isNodata = true;
    const tableOption: any[] = [
      {
        key: 'date',
        header: formatMessage({ id: 'overview_table_date' }),
        align: 'left',
        fixed: 'left',
        width: 160,
        render: (cvm: any) => {
          const weekDay = getWeekDay(cvm?.date);
          const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六';
          return <>
            <span className='table-text'>{cvm?.date}</span>
            {timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
          </>;
        },
      }
    ];
    // 趋势图
    if (showBarChart || showLineChart) {
      componentMetrics?.map((metric: any) => {
        const {
          metricName,
          legendName,
          tableName,
          compareName,
          compareLegendName,
          compareTableName,
        } = getMetricsName(metric, isEn) || {};
        const { metricKey, reqMetricKey, isCompare, } = metric || {};
        const seriesObj: any = {
          name: metricName,
          data: [],
        };
        const compareSeriesObj: any = {
          name: compareName,
          data: [],
        };

        metricData?.map((item: any) => {
          // 兼容传参跟后端返回值不一致的情况
          const value = reqMetricKey ? item?.metrics?.[reqMetricKey]?.value : item?.metrics?.[metricKey]?.value;
          const compareValue = reqMetricKey ? item?.metrics?.[reqMetricKey]?.last_value : item?.metrics?.[metricKey]?.last_value;
          seriesObj.data?.push(value || '');
          compareSeriesObj.data?.push(compareValue || '');
          if (value || isCompare && compareValue) isNodata = false;
        });
        // 默认逻辑
        legendData?.push(legendName);
        showBarChart && barSeries?.push({
          type: 'bar',
          barGap: '5%',
          ...seriesObj
        });
        showLineChart && lineSeries?.push({
          type: 'line',
          symbol: 'circle',
          ...seriesObj
        });
        // 对比趋势图逻辑
        if (isCompare) {
          legendData?.push(compareLegendName);
          showBarChart && barSeries?.push({
            type: 'bar',
            barGap: '5%',
            ...compareSeriesObj
          });
          showLineChart && lineSeries?.push({
            type: 'line',
            symbol: 'circle',
            lineStyle: {
              type: "dotted",
            },
            ...compareSeriesObj
          });
        }
        // 表格
        if (showTable || showDownloadTable) {
          tableOption?.push({
            key: metricKey,
            header: tableName,
            align: 'right',
          });
          // 对比趋势图逻辑
          if (isCompare) {
            tableOption?.push({
              key: `${metricKey}_compare`,
              header: compareTableName,
              align: 'right',
            });
          }
        }
      });
    }

    // 表格
    if (showTable || showDownloadTable) {
      metricData?.map((item: any) => {
        const date = timeType === 'Month' ? moment(item?.date).format('YYYY-MM') : moment(item?.date).format('YYYY-MM-DD');
        const tableObj: any = { date };
        const downTableObj: any = { date };
        componentMetrics?.map((metric: any) => {
          const { metricKey, reqMetricKey, formatRule, isCompare } = metric || {};
          const value = reqMetricKey ? item?.metrics?.[reqMetricKey]?.value : item?.metrics?.[metricKey]?.value;
          const compareValue = reqMetricKey ? item?.metrics?.[reqMetricKey]?.last_value : item?.metrics?.[metricKey]?.last_value;
          tableObj[metricKey] = dataFormat({ value, formatRule });
          downTableObj[metricKey] = downValueFormatter(value, metric);
          // 对比趋势图逻辑
          if (isCompare) {
            tableObj[`${metricKey}_compare`] = dataFormat({ value: compareValue, formatRule });
            downTableObj[`${metricKey}_compare`] = downValueFormatter(compareValue, metric);
          }
        });
        tableData?.push(tableObj);
        tableDownloadData?.push(downTableObj);
      });
    }
    const lineSeriesData = lineSeries.map((item, index)=> {
      return {...item, color: chartAllStudioColors[index]}
    })
    return {
      xAxisData,
      legendData,
      barSeries,
      lineSeries: lineSeriesData,
      isNodata,
      table: propsTable || {
        option: tableOption,
        data: isReverse ? tableData?.reverse() : tableData,
        downloadData: isReverse ? tableDownloadData?.reverse() : tableDownloadData
      }
    };
  }, [componentMetrics, metricData, showConfig, propsTable, isEn]);

  const formatterFunc = (params: any) => {
    const date = moment(params?.[0]?.axisValue).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD');
    const data = params?.map((item: { seriesName: any; value: number; color: any; }, index: number) => {
      const { seriesName, value, color } = item || {};
      const metric = bubbleMetrics?.find(v => v?.name === seriesName);
      const { formatRule, toolTipName } = metric || componentMetrics?.[index] || {};
      return {
        name: toolTipName,
        metricsNum: dataFormat({ value, formatRule }),
        color,
      };
    });
    return getEchartTooltipText({
      title: date,
      metricsName: showBubbleTitle ? bubbleTitle : undefined,
      data,
    });
  };


  const configFunc = (option: any) => {
    let opt = xAxisLabelFormat(option, { xAxisLabelFormat: handleAxisFormat });
    const metricFormatRule = componentMetrics?.[0]?.formatRule;
    if (showEye) {
      const formatRule = labelFormatRule || metricFormatRule || 'thousandsInit';
      opt = addChartLabel(opt, {
        formatter: (params: any) => dataFormat({ value: params?.value, formatRule }),
        show: iseye,
        formatNum: (value: any) => dataFormat({ value, formatRule }),
      });
    }
    opt = xAxisHighLight(opt, {
      formatter: (params: { value: string; }) => handleAxisFormat(params?.value),
    });
    // y轴格式化
    if (opt?.yAxis?.axisLabel?.formatter) {
      const formatter = yAxisFormatRule || metricFormatRule || 'bmk0';
      opt.yAxis.axisLabel.formatter = (value: number) => axisLabelFormatter(value, { formatter, scalar_type: "" });
    }
    if (setOtherConfigFunc) opt = setOtherConfigFunc(opt, { iseye, isEvent });
    return opt;
  };

  const renderList = [
    showEvent && {
      type: 'event',
      svgType: isEvent ? 'move-left' : 'move-right',
      content: isEvent ? formatMessage({ id: 'common_show_event' }) : formatMessage({ id: 'common_hide_event' }),
      isHideCom: true,
    },
    showEye && {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    showBarChart && {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={height}
        legendData={!isNodata ? legendData : []}
        xAxisData={!isNodata ? xAxisData : []}
        series={!isNodata ? barSeries : []}
        formatterFunc={toolTipFunc ? (params: any) => toolTipFunc(params, { ...props, title, subtitle, downloadTitle, bubbleTitle, bubbleMetrics }) : formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          opt = configFunc(opt);
          return opt;
        }}
      />
    },
    showLineChart && {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={height}
        legendData={!isNodata ? legendData : []}
        xAxisData={!isNodata ? xAxisData : []}
        series={!isNodata ? lineSeries : []}
        formatterFunc={toolTipFunc ? (params: any) => toolTipFunc(params, { ...props, title, subtitle, downloadTitle, bubbleTitle, bubbleMetrics }) : formatterFunc}
        setOtherConfigFunc={(opt: any) => {
          opt = configFunc(opt);
          return opt;
        }}
      />
    },
    showTable && {
      type: 'sheet',
      svgType: 'sheet',
      content: formatMessage({ id: 'common_table' }),
      component: <MiniTable
        height={height}
        loading={false}
        tableOption={table?.option}
        tableList={table?.data}
      />
    }
  ]?.filter(v => v !== false);

  return isOnlyChart ?
    renderList?.[defaultAvtive].component :
    <CardBox
      className={`register-table dbs-metrics-trend-chart dbd-metrics-trend-chart ${className}`}
      style={style}
      titleCom={
        <div>
          {title}
          {subtitle &&
            <span className='dbd-metrics-trend-chart-subtitle'>
              {subtitle}
            </span>}
        </div>
      }
      height={height}
      bordered={bordered}
      renderList={renderList}
      defaultAvtive={defaultAvtive}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye);
        }
        if (type === 'event') {
          setIsEvent(!isEvent);
        }
        if (setIconType) {
          setIconType(type);
        }
      }}
      downloadClick={async () => {
        handleDownload(table, downloadTitle);
      }}
      isLoading={loading}
      isError={error}
      isNodata={isNodata}
    />;
};

export default MetricsTrendChart;